import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, Clock, User, Music, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface PendingRequest {
  id: string;
  singer_name: string;
  custom_song_title: string;
  custom_song_artist: string;
  created_at: string;
  approval_status: string;
}

interface CustomSongApprovalProps {
  venueId: string;
  showExportControls?: boolean;
}

const CustomSongApproval = ({ venueId, showExportControls = true }: CustomSongApprovalProps) => {
  const { toast } = useToast();
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [approvalFilter, setApprovalFilter] = useState<'all'|'pending'|'approved'|'rejected'>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    if (venueId) {
      loadPendingRequests();
      
      // Set up real-time subscription for new custom song requests
      const subscription = supabase
        .channel('custom_song_approvals')
        .on('postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'requests',
            filter: `venue_id=eq.${venueId}`
          },
          (payload) => {
            console.log('Custom song request change:', payload);
            loadPendingRequests();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [venueId]);

  const loadPendingRequests = async () => {
    try {
      const resp = await (supabase.from('requests') as any)
        .select('id, singer_name, custom_song_title, custom_song_artist, created_at, approval_status')
        .eq('venue_id', venueId)
        .eq('approval_status', 'pending')
        .is('song_id', null)
        .order('created_at', { ascending: true });

      if (resp.error) throw resp.error;

      const rows: any[] = resp.data || [];
      const normalized: PendingRequest[] = rows.map((r) => ({
        id: r.id,
        singer_name: r.singer_name || '',
        custom_song_title: r.custom_song_title || '',
        custom_song_artist: r.custom_song_artist || '',
        created_at: r.created_at || '',
        approval_status: r.approval_status || 'pending',
      }));

      setPendingRequests(normalized);
    } catch (error) {
      console.error('Error loading pending requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (requestId: string, approved: boolean) => {
    setProcessingId(requestId);
    try {
      const updates: any = {
        approval_status: approved ? 'approved' : 'rejected'
      };

      // If approved, add to queue
      if (approved) {
        // Get the next queue position
        const { data: queueData } = await supabase
          .from('requests')
          .select('queue_position')
          .eq('venue_id', venueId)
          .eq('status', 'queued')
          .order('queue_position', { ascending: false })
          .limit(1);

        const nextPosition = queueData && queueData.length > 0 
          ? queueData[0].queue_position + 1 
          : 1;

        updates.status = 'queued';
        updates.queue_position = nextPosition;
      } else {
        // The requests.status column has a DB check constraint limiting allowed values.
        // Use 'removed' to take the request out of the active queue instead of 'rejected'.
        updates.status = 'removed';
      }

      const { error } = await supabase
        .from('requests')
        .update(updates)
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: approved ? "Request Approved" : "Request Rejected",
        description: approved 
          ? "Custom song has been added to the queue" 
          : "Custom song request has been rejected",
      });

      // Optimistically remove the request from the pending list so UI updates immediately
      setPendingRequests((prev) => prev.filter((r) => r.id !== requestId));

      // Signal other tabs / projection to refresh the queue if we approved (moved to queued)
      try {
        if (approved) {
          const { sendQueueUpdate } = await import('@/lib/queueSignal');
          sendQueueUpdate({ action: 'approved', requestId });
        }
      } catch (err) {
        // ignore
      }

      // Refresh pending list to ensure consistency
      loadPendingRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      toast({
        title: "Error",
        description: "Failed to update request",
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Custom Song Approvals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Loading pending requests...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Custom Song Approvals
          {pendingRequests.length > 0 && (
            <Badge variant="secondary" className="bg-console-signal/15 text-console-signal border border-console-signal/30 font-geist-mono text-[10px] tracking-[0.14em] rounded-[3px]">
              {pendingRequests.length} pending
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      {showExportControls && (
        <div className="px-4 -mt-4 mb-2 flex items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Filter:</label>
          <select
            value={approvalFilter}
            onChange={(e) => setApprovalFilter(e.target.value as any)}
            className="px-2 py-1 bg-background border border-border rounded"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-2 py-1 bg-background border border-border rounded" />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-2 py-1 bg-background border border-border rounded" />
        </div>

        <Button
          size="sm"
          variant="outline"
          onClick={async () => {
            setDownloadLoading(true);
            try {
              // build query dynamically and use any to avoid deep typing issues
              let query: any = supabase
                .from('requests')
                .select(`
                  id,
                  singer_name,
                  custom_song_title,
                  custom_song_artist,
                  approval_status,
                  status,
                  queue_position,
                  created_at
                `)
                .eq('venue_id', venueId);

              if (approvalFilter && approvalFilter !== 'all') {
                query = query.eq('approval_status', approvalFilter);
              }
              if (startDate) {
                query = query.gte('created_at', startDate);
              }
              if (endDate) {
                // include full day
                query = query.lte('created_at', `${endDate}T23:59:59`);
              }

              const resp: any = await query.order('created_at', { ascending: false });
              if (resp.error) throw resp.error;

              const rows: any[] = resp.data || [];
              if (rows.length === 0) {
                toast({ title: 'No requests', description: 'No past requests found for this venue' });
                return;
              }

              const headers = ['Request ID','Singer','Song Title','Song Artist','Approval Status','Status','Queue Position','Requested At'];
              const csv = [headers.join(',')]
                .concat(rows.map((r: any) => {
                  const vals = [
                    r.id,
                    r.singer_name,
                    r.custom_song_title || '',
                    r.custom_song_artist || '',
                    r.approval_status || '',
                    r.status || '',
                    r.queue_position ?? '',
                    r.created_at || ''
                  ];
                  return vals.map((v) => {
                    const s = String(v ?? '');
                    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
                      return `"${s.replace(/"/g, '""')}"`;
                    }
                    return s;
                  }).join(',');
                })).join('\n');

              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const link = document.createElement('a');
              const url = URL.createObjectURL(blob);
              link.setAttribute('href', url);
              const filename = `venue_${venueId || 'unknown'}_requests_${new Date().toISOString().split('T')[0]}.csv`;
              link.setAttribute('download', filename);
              link.style.visibility = 'hidden';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              toast({ title: 'Exported', description: `Downloaded ${rows.length} requests` });
            } catch (err) {
              console.error('Error exporting requests:', err);
              toast({ title: 'Error', description: 'Failed to download requests', variant: 'destructive' });
            } finally {
              setDownloadLoading(false);
            }
          }}
          disabled={downloadLoading}
        >
          <Download className="h-4 w-4 mr-2" />
          {downloadLoading ? 'Exporting...' : 'Download Past Requests'}
        </Button>
        </div>
      )}
      <CardContent>
        {pendingRequests.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No pending custom song requests</p>
            <p className="text-sm">Custom song requests will appear here for approval</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingRequests.map((request) => (
              <div
                key={request.id}
                className="border border-console-signal/30 rounded-[6px] p-4 bg-console-signal/5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Music className="h-4 w-4 text-console-signal" />
                      <h4 className="font-semibold text-console-signal">Custom Song Request</h4>
                      <Badge variant="outline" className="border-console-signal/30 text-console-signal">
                        Pending Approval
                      </Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium">Song:</span>
                        <span className="ml-2 text-sm">{request.custom_song_title}</span>
                      </div>
                      <div>
                        <span className="text-sm font-medium">Artist:</span>
                        <span className="ml-2 text-sm">{request.custom_song_artist}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Requested by: {request.singer_name}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(request.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 sm:ml-4 sm:shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleApproval(request.id, true)}
                      className="flex-1 sm:flex-none h-11 sm:h-9 px-4 font-geist font-semibold text-[13px] bg-console-ok text-console-bg hover:bg-console-ok/90 rounded-[6px]"
                      disabled={processingId === request.id}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      {processingId === request.id ? 'Processing...' : 'Approve'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleApproval(request.id, false)}
                      className="flex-1 sm:flex-none h-11 sm:h-9 px-4 font-geist font-semibold text-[13px] text-console-red border border-console-red/30 hover:bg-red-500/10 hover:text-console-red rounded-[6px] bg-transparent"
                      disabled={processingId === request.id}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      {processingId === request.id ? 'Processing...' : 'Reject'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomSongApproval;
